import React from 'react';
import PropTypes from 'prop-types';
import { CalendarIcon } from 'lucide-react';
import Locales from 'locales';
import { formatDate } from 'helpers';
import { Form, FormField, FormItem, FormLabel, FormControl, FormDescription, FormMessage } from 'components/ui/form';
import { Input } from 'components/ui/input';
import { Button } from 'components/ui/button';
import { Card } from 'components/ui/card';
import { Calendar } from 'components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from 'components/ui/popover';
import { Spinner } from 'components/ui/spinner';

const CONST_MIN_CAMPAIGN_YEAR = 2023;

const PreferencesForm = ({ form, onSubmit, currentYear, isLoading = false, campaigns, isNewCampaign = false }) => {
  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className={`${isNewCampaign ? 'space-y-2' : 'space-y-6'}`}>
        <Card
          className={`bg-white ${isNewCampaign ? 'shadow-none rounded-none border-0 p-0 space-y-3' : 'p-6 space-y-6'}`}
        >
          <h4 className="text-lg font-semibold text-gray-900">Général</h4>

          <FormField
            control={form.control}
            name="campaign_year"
            rules={{
              required: Locales.preferences.error.invalid,
              min: {
                value: CONST_MIN_CAMPAIGN_YEAR,
                message: Locales.preferences.error.out_of_range,
              },
              pattern: {
                value: /^[0-9]+$/,
                message: Locales.preferences.error.invalid,
              },
              validate: (value) => {
                const yearExists = campaigns?.some((c) => String(c.year) === String(value));
                return yearExists ? 'Cette année de campagne existe déjà' : true;
              },
            }}
            disabled={!isNewCampaign}
            render={({ field }) => (
              <FormItem>
                <FormLabel>{Locales.preferences.field.campaign_year}</FormLabel>
                <FormControl>
                  <Input type="number" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="pilgrim_eligibility_min_age"
            rules={{
              required: Locales.preferences.error.invalid,
              pattern: { value: /^[0-9]+$/, message: Locales.preferences.error.invalid },
            }}
            render={({ field }) => (
              <FormItem>
                <FormLabel>{Locales.preferences.field.min_eligibility_age}</FormLabel>
                <FormControl>
                  <Input type="number" {...field} />
                </FormControl>
                <FormDescription>{Locales.preferences.field.helptext.min_eligibility_age}</FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="registration_deposit"
            rules={{
              required: Locales.preferences.error.invalid,
              pattern: { value: /^[0-9]+$/, message: Locales.preferences.error.invalid },
            }}
            render={({ field }) => (
              <FormItem>
                <FormLabel>{Locales.preferences.field.pre_registration_fee}</FormLabel>
                <FormControl>
                  <div className="flex items-stretch">
                    <span className="inline-flex items-center px-3 border border-r-0 border-gray-300 rounded-l-md bg-gray-100 text-gray-700">
                      CFA
                    </span>
                    <Input type="number" className="rounded-l-none" {...field} />
                  </div>
                </FormControl>
                <FormDescription>{Locales.preferences.field.helptext.pre_registration_fee}</FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="campaign_registration_end_date"
            rules={{
              required: 'Veuillez choisir une date de fin des enregistrements.',
            }}
            render={({ field }) => (
              <FormItem className="flex flex-col">
                <FormLabel>Date de fin des enregistrements</FormLabel>
                <Popover>
                  <PopoverTrigger asChild>
                    <FormControl>
                      <Button variant="outline">
                        {field.value ? formatDate(field.value, 'dd/MM/yyyy') : <span>Sélectionner une date</span>}
                        <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                      </Button>
                    </FormControl>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={field.value}
                      onSelect={field.onChange}
                      fromYear={currentYear}
                      toYear={currentYear + 10}
                      disabled={(date) => {
                        const today = new Date();
                        today.setHours(0, 0, 0, 0); // reset hours to midnight
                        return date < today;
                      }}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
                <FormDescription>Date après laquelle aucun enregistrement ne sera possible.</FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />
        </Card>

        <Card
          className={`bg-white ${isNewCampaign ? 'shadow-none rounded-none border-0 p-0 space-y-3' : 'p-6 space-y-6'}`}
        >
          <h4 className="text-lg font-semibold text-gray-900">Quotas</h4>

          <FormField
            control={form.control}
            name="total_pilgrim_quota"
            rules={{
              required: Locales.preferences.error.invalid,
              pattern: { value: /^[0-9]+$/, message: Locales.preferences.error.invalid },
              min: { value: 1, message: Locales.preferences.error.invalid_min_ratio },
            }}
            render={({ field }) => (
              <FormItem>
                <FormLabel>Nombre de place disponible</FormLabel>
                <FormControl>
                  <Input type="number" {...field} />
                </FormControl>
                <FormDescription>Nombre total de places disponibles pour la campagne</FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="certificate_min_active_pilgrim"
            rules={{
              required: Locales.preferences.error.invalid,
              pattern: { value: /^[0-9]+$/, message: Locales.preferences.error.invalid },
            }}
            render={({ field }) => (
              <FormItem>
                <FormLabel>{Locales.preferences.field.minimum_pilgrims}</FormLabel>
                <FormControl>
                  <Input type="number" {...field} />
                </FormControl>
                <FormDescription>{Locales.preferences.field.helptext.minimum_pilgrims}</FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />
        </Card>
        <Card
          className={`bg-white ${isNewCampaign ? 'shadow-none rounded-none border-0 p-0 space-y-3' : 'p-6 space-y-6'}`}
        >
          <h4 className="text-lg font-semibold text-gray-900">Accompagnateurs</h4>

          <FormField
            control={form.control}
            name="religious_supervisors_ratio"
            rules={{
              required: Locales.preferences.error.invalid,
              pattern: { value: /^[0-9]+$/, message: Locales.preferences.error.invalid },
              min: { value: 1, message: Locales.preferences.error.invalid_min_ratio },
            }}
            render={({ field }) => (
              <FormItem>
                <FormLabel>Pèlerins autorisant un encadreur réligieux</FormLabel>
                <FormControl>
                  <Input type="number" {...field} />
                </FormControl>
                <FormDescription>
                  Nombre de pèlerins autorisant une société à enregistrer un encadreur réligieux
                </FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="religious_supervisors_quota"
            rules={{
              required: Locales.preferences.error.invalid,
              pattern: { value: /^[0-9]+$/, message: Locales.preferences.error.invalid },
              min: { value: 1, message: Locales.preferences.error.invalid_min_ratio },
            }}
            render={({ field }) => (
              <FormItem>
                <FormLabel>Nombre d&apos;encadreur réligieux</FormLabel>
                <FormControl>
                  <Input type="number" {...field} />
                </FormControl>
                <FormDescription>Nombre total d&apos;encadreur réligieux autorisé pour la campagne</FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />

          <div className="border-t mt-4" />

          <FormField
            control={form.control}
            name="medical_supervisors_ratio"
            rules={{
              required: Locales.preferences.error.invalid,
              pattern: { value: /^[0-9]+$/, message: Locales.preferences.error.invalid },
              min: { value: 1, message: Locales.preferences.error.invalid_min_ratio },
            }}
            render={({ field }) => (
              <FormItem>
                <FormLabel>Pèlerins autorisant un encadreur médical</FormLabel>
                <FormControl>
                  <Input type="number" {...field} />
                </FormControl>
                <FormDescription>
                  Nombre de pèlerins autorisant une société à enregistrer un encadreur médical
                </FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="medical_supervisors_quota"
            rules={{
              required: Locales.preferences.error.invalid,
              pattern: { value: /^[0-9]+$/, message: Locales.preferences.error.invalid },
              min: { value: 1, message: Locales.preferences.error.invalid_min_ratio },
            }}
            render={({ field }) => (
              <FormItem>
                <FormLabel>Nombre d&apos;encadreur médical</FormLabel>
                <FormControl>
                  <Input type="number" {...field} />
                </FormControl>
                <FormDescription>Nombre total d&apos;encadreur médical autorisé pour la campagne</FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />
        </Card>

        {!isNewCampaign && (
          <div className="flex justify-end">
            <Button type="submit" variant="hajj" disabled={isLoading}>
              {isLoading && <Spinner />}
              {Locales.base.button.save}
            </Button>
          </div>
        )}
      </form>
    </Form>
  );
};

PreferencesForm.propTypes = {
  form: PropTypes.shape({}).isRequired,
  onSubmit: PropTypes.func.isRequired,
  currentYear: PropTypes.number,
  isLoading: PropTypes.bool,
};

export default PreferencesForm;
